/**
 * The submitted action, held.
 *
 * An applied action is an intent, not an event: the server can still refuse it, and it
 * only lands when the phase closes. So the board keeps the aim it was given lit under
 * its own id and says nothing more — no impact, no shake, no traces. The id is
 * deliberately never counted as played: the resolution reel is what draws the action,
 * once, when the server has actually resolved it.
 *
 * Two rules hold this module together:
 *
 *  - A PREVIEW EFFECT HAS ONE DEFINITION. `previewEffect` builds it, and both the live
 *    aim under the pointer and the marker held after submit go through it, so the held
 *    aim cannot drift away from the aim the player was shown while choosing.
 *
 *  - ABSENCE FROM THE LOG IS NOT PROOF OF REJECTION. The log is a paginated window that
 *    a failed fetch can leave stale and a busy round can scroll an action out of. So a
 *    marker is dropped on absence only when the caller vouches for the fetch (`trusted`)
 *    AND the window brackets the cycle the action was submitted in.
 *
 * Pure: no Vue, no three.js, no DOM. The board supplies the stage, where its tokens
 * stand and what its party can see; everything else is decided here.
 */

import { castsForAction, cycleNumberOf, drawsAnImpact } from './reel';

export const PENDING_ACTION_PREFIX = 'pending:';

/**
 * The aim's share of the cast's own intensity.
 *
 * A telegraph, not a rehearsal: a player has to be able to tell an aim from the thing
 * having already happened, and the whole difference is weight. At 0.7 against the
 * stage's own preview activation the material is unmistakably present and still
 * visibly held back.
 */
export const AIM_PREVIEW_INTENSITY = 0.7;

/**
 * One cast as the stage draws an aim: its own material, faintly, on the ground it
 * would cover, held in the `preview` phase under a caller-chosen id.
 *
 * `cells` is separate from `cast.cells` because the board masks to what the party can
 * see after the cast is built, and the fog is painted below this layer — a cell the
 * server withheld cannot be covered up once it is drawn.
 */
export function previewEffect(cast, { id, cells = cast?.cells || [] } = {}) {
  const origin = cast.origin || cells[0];
  const caster = cast.caster || null;
  const dx = origin.col - (caster?.col ?? origin.col);
  const dy = origin.row - (caster?.row ?? origin.row);
  const reach = Math.hypot(dx, dy) || 1;
  return {
    id,
    violationType: cast.violationType,
    intent: cast.intent,
    cells,
    origin,
    direction: { x: dx / reach, z: dy / reach },
    footprintHint: cast.footprintHint,
    mergeMode: cast.mergeMode,
    intensity: cast.intensity * AIM_PREVIEW_INTENSITY,
    seed: cast.seed,
    size: cast.size,
    phase: 'preview',
    aim: true,
    /* The trail's launch point. Without it there is no flight, only a stain. */
    caster,
  };
}

/**
 * Whether this action is one the board holds an aim for.
 *
 * The boundary is stated here rather than left to `castsForAction` returning nothing:
 * a walk, an inspection and a dice roll are actions whose whole story is the token or
 * the panel, and they must not reach the marker at all.
 */
export function holdsPendingAim(action) {
  if (!action?.id || !drawsAnImpact(action)) return false;
  const aims = action?.data?.tactical_aims;
  if (Array.isArray(aims) && aims.length) return true;
  return Array.isArray(action?.targets) && action.targets.length > 0;
}

/**
 * Light the submitted aim and remember it under the action's id.
 *
 * `cycle` is the cycle the action was submitted in; it is what later tells a rejection
 * apart from an action the log window has simply scrolled past. `ability` comes from
 * the caller rather than off the response because the submit serializer sends `skill`
 * as a bare id and no impacts — without it a heal, a ward and a sword swing all render
 * as the same identity-less grey harm.
 */
export function markPendingAction({
  stage,
  pending,
  action,
  ability = null,
  cycle = null,
  positionOf = () => null,
  maskCells = cells => cells,
}) {
  if (!stage || !pending || !holdsPendingAim(action)) return null;

  const casts = castsForAction(action, { positionOf, ability });
  if (!casts.length) return null;

  clearPendingAction({ stage, pending, actionId: action.id });
  const ids = casts.map(cast => {
    const cells = maskCells(cast.cells) || [];
    if (!cells.length) return null;
    const id = `${PENDING_ACTION_PREFIX}${cast.id}`;
    stage.setEffect(previewEffect(cast, { id, cells }));
    stage.setPhase('preview', id);
    return id;
  }).filter(Boolean);

  if (!ids.length) return null;
  pending.set(action.id, { ids, cycle: Number.isFinite(cycle) ? cycle : null });
  return ids;
}

/** The action is no longer awaiting anything — it resolved, or it was refused. */
export function clearPendingAction({ stage, pending, actionId }) {
  const held = pending?.get(actionId);
  if (!held) return false;
  held.ids.forEach(id => stage?.removeEffect(id));
  pending.delete(actionId);
  return true;
}

export function clearAllPendingActions({ stage, pending }) {
  [...(pending?.keys() || [])].forEach(actionId => clearPendingAction({ stage, pending, actionId }));
}

/**
 * A held action leaves the marker either way: the reel drew it, or the server dropped
 * it. Telling the second case from a log that simply cannot answer is the whole job
 * here.
 *
 * An untrusted log — a fetch that failed, leaving the previous window in place — is
 * evidence of nothing and clears nothing. A trusted log clears a marker it does not
 * carry only when the window BRACKETS the action's cycle: it reaches back before that
 * cycle, so nothing from it is truncated away, and it reaches forward to at least it,
 * so the cycle has actually been read. A window lying wholly behind the marker — a log
 * that has not caught up, or a slow response landing after a faster newer one — spans
 * nothing and answers nothing. Otherwise the marker is released once the log's newest
 * cycle has moved past it, by which time the action has been drawn or is beyond reach.
 *
 * A marker with no cycle takes the window's newest cycle as its stamp instead of being
 * dropped: it was submitted no later than that, so the stamp cannot retire it early,
 * and one more window is enough to adjudicate it.
 */
export function reconcilePendingActions({ stage, pending, log, drawnIds = [], trusted = false }) {
  drawnIds.forEach(actionId => clearPendingAction({ stage, pending, actionId }));
  if (!trusted || !pending?.size) return;

  const entries = (Array.isArray(log) ? log : []).filter(Boolean);
  const live = new Set(entries.map(entry => entry.id));
  const cycles = entries.map(cycleNumberOf).filter(Number.isFinite);
  const oldest = cycles.length ? Math.min(...cycles) : null;
  const newest = cycles.length ? Math.max(...cycles) : null;

  [...pending.entries()].forEach(([actionId, held]) => {
    if (live.has(actionId)) return;
    const cycle = held.cycle;
    if (!Number.isFinite(cycle)) {
      if (newest !== null) held.cycle = newest;
      return;
    }
    const brackets = oldest !== null && newest !== null && oldest < cycle && cycle <= newest;
    const overtaken = newest !== null && newest > cycle;
    if (brackets || overtaken) clearPendingAction({ stage, pending, actionId });
  });
}
